/**
 * Assembly Videos (E20-1058)
 *
 * A category selector (one ARIA tablist across all four columns) above a single
 * region holding the selected product's video and transcript. The region starts
 * hidden and is revealed on the first selection; selecting another product swaps
 * the content in place rather than adding a second region.
 *
 * Only one <iframe> ever exists. Each tab carries its product's YouTube id and
 * poster, so selecting a product just re-points the facade — the embed itself is
 * built on play. That keeps ten products from meaning ten YouTube iframes, and
 * means nothing is requested from YouTube until someone asks for it.
 *
 * Transcripts are server-rendered (rich text can't be assembled in JS) and
 * toggled by id. A product with no transcript is a supported state: the panel is
 * dropped and the video takes the full width — three of the launch treadmills
 * have no transcript.
 */
class AssemblyVideos extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.stage = this.querySelector('[data-stage]');
    if (!this.tabs.length || !this.stage) return;

    this.player = this.stage.querySelector('[data-player]');
    this.playButton = this.stage.querySelector('[data-play]');
    this.posterImg = this.stage.querySelector('[data-poster-img]');
    this.playLabel = this.stage.querySelector('[data-play-label]');
    // NB: HTMLElement already defines a string `title` property, so assigning an
    // element to it silently stringifies. Hence the stageTitle name.
    this.stageTitle = this.stage.querySelector('[data-stage-title]');
    this.transcript = this.stage.querySelector('[data-transcript]');
    this.bodies = Array.from(this.stage.querySelectorAll('[data-panel-body]'));

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i), { signal });
    });
    this.addEventListener('keydown', (e) => this.onKeydown(e), { signal });
    this.playButton?.addEventListener('click', () => this.play(), { signal });

    this.current = -1;
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  select(index) {
    const tab = this.tabs[index];
    if (!tab || index === this.current) return;
    this.current = index;

    // Roving tabindex: only the active tab is a tab stop, so Tab doesn't walk all ten.
    this.tabs.forEach((t) => {
      const isActive = t === tab;
      t.setAttribute('aria-selected', String(isActive));
      t.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    const name = tab.dataset.title || '';
    if (this.stageTitle) this.stageTitle.textContent = name;
    if (this.playLabel) {
      const template = window.theme?.strings?.assemblyPlay || 'Play the [product] assembly video';
      this.playLabel.textContent = template.replace('[product]', name);
    }
    // Label the panel by its active tab, not a literal aria-label — the tabs pattern
    // the theme uses elsewhere (bfx-media-selector) and it survives a renamed product.
    if (tab.id) this.stage.setAttribute('aria-labelledby', tab.id);

    // Back to the poster: a previous selection may have swapped in an iframe.
    this.reset(tab);

    // Show this product's transcript, if it has one.
    const id = tab.dataset.panel;
    let hasTranscript = false;
    this.bodies.forEach((body) => {
      const match = body.id === id;
      body.hidden = !match;
      if (match) hasTranscript = true;
    });
    this.transcript.hidden = !hasTranscript;
    this.stage.classList.toggle('is-video-only', !hasTranscript);

    this.stage.hidden = false;

    // Bring the region into view, as the Schwinn page does — the selector is tall
    // enough on mobile that the swap happens off screen otherwise. Focus moves
    // first (without scrolling) so screen-reader and keyboard users land there
    // too, then the offset scroll runs.
    this.stage.focus({ preventScroll: true });
    window.bfxAnchorScroll?.scrollToTarget(this.stage, false);
  }

  // Drop any live embed and put the facade back, pointed at this product.
  reset(tab) {
    this.player.querySelector('iframe')?.remove();
    if (this.playButton) this.playButton.hidden = false;
    if (this.posterImg) {
      const poster = tab.dataset.poster;
      if (poster) {
        this.posterImg.src = poster;
      } else {
        this.posterImg.removeAttribute('src');
      }
    }
    this.videoId = tab.dataset.videoId || '';
  }

  // Build the embed only once the shopper asks for it, then hand it focus.
  play() {
    if (!this.videoId || this.player.querySelector('iframe')) return;

    const iframe = document.createElement('iframe');
    const params = new URLSearchParams({
      autoplay: '1',
      playsinline: '1',
      rel: '0',
      modestbranding: '1',
    });
    iframe.src = `https://www.youtube-nocookie.com/embed/${this.videoId}?${params}`;
    iframe.title =
      this.stageTitle?.textContent || window.theme?.strings?.assemblyVideoTitle || 'Assembly video';
    iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.className = 'bfx-assembly-videos__iframe';

    this.playButton.hidden = true;
    this.player.appendChild(iframe);
    iframe.focus({ preventScroll: true });
  }

  // Roving selection across the whole selector, matching the theme's other tablists.
  onKeydown(event) {
    const from = this.tabs.indexOf(document.activeElement);
    if (from === -1) return;

    const keys = {
      ArrowRight: from + 1,
      ArrowDown: from + 1,
      ArrowLeft: from - 1,
      ArrowUp: from - 1,
      Home: 0,
      End: this.tabs.length - 1,
    };
    const next = keys[event.key];
    if (next === undefined) return;

    event.preventDefault();
    const target = this.tabs[(next + this.tabs.length) % this.tabs.length];

    // Move the tab stop with focus, or Tab would return to whichever tab was last
    // selected rather than the one the arrow keys landed on.
    this.tabs.forEach((t) => t.setAttribute('tabindex', t === target ? '0' : '-1'));
    target.focus();
  }
}

if (!customElements.get('assembly-videos')) {
  customElements.define('assembly-videos', AssemblyVideos);
}
